import { useMemo } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  GET_USERS,
  UPDATE_USER,
  DELETE_USER,
  GetUsersQueryResponse,
  UpdateUserMutationResponse,
  UpdateUserMutationVariables,
  DeleteUserMutationResponse,
  DeleteUserMutationVariables,
} from './Users';
import { AUTH_API_URL } from './SpaceAssignments';

export interface UserTableRecord {
  key: string;
  id: string;
  name: string;
  email: string;
  role: string;
  lastActiveAt?: string;
}

export interface EditingData {
  name: string;
  role: string;
}

const AUTH_API_CONTEXT = { context: { uri: AUTH_API_URL } } as const;

export const useUsersData = () => {
  const { data, loading, error, refetch } = useQuery<GetUsersQueryResponse>(GET_USERS, {
    variables: { limit: 100, offset: 0 },
    ...AUTH_API_CONTEXT,
  });

  const [updateUser, { loading: isUpdating }] = useMutation<
    UpdateUserMutationResponse,
    UpdateUserMutationVariables
  >(UPDATE_USER, AUTH_API_CONTEXT);

  const [deleteUser] = useMutation<DeleteUserMutationResponse, DeleteUserMutationVariables>(
    DELETE_USER,
    AUTH_API_CONTEXT,
  );

  // Exclude superusers from the visible list — they are Butlr-internal and not
  // part of the org's user roster. `hiddenSuperuserCount` is surfaced so an
  // admin who just invited one doesn't think the user vanished.
  const isSuperuserRole = (user: { roles?: string[] | null }) =>
    !!user.roles?.some((r) => r.toLowerCase() === 'superuser');

  const { tableData, hiddenSuperuserCount } = useMemo(() => {
    const users = data?.users?.users || [];
    const rows: UserTableRecord[] = users
      .filter((user) => !isSuperuserRole(user))
      .map((user) => ({
        key: user.id,
        id: user.id,
        name: user.name,
        email: user.email,
        role: user.roles?.some((r) => r.toLowerCase() === 'owner')
          ? 'Owner'
          : user.roles?.some((r) => r.toLowerCase() === 'admin')
          ? 'Admin'
          : 'Member',
        lastActiveAt: user.last_login || undefined,
      }));
    return { tableData: rows, hiddenSuperuserCount: users.filter(isSuperuserRole).length };
  }, [data]);

  return {
    tableData,
    hiddenSuperuserCount,
    isLoading: loading,
    error,
    refetch,
    updateUser,
    isUpdating,
    deleteUser,
  };
};
